
import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import {
  getAuth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  type ConfirmationResult,
  type UserCredential,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: "AIzaSyBshE1wx5Qq-SERkU0E9cWMFG6UA-I6f6k",
  authDomain: "kfm-sms.firebaseapp.com",
  projectId: "kfm-sms",
  storageBucket: "kfm-sms.firebasestorage.app",
  messagingSenderId: "55913119934",
  appId: "1:55913119934:web:07fbdf87274eb027cedeef",
};

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const messaging = getMessaging(app);

export const requestNotificationPermission = async () => {
  try {
    const permission = await Notification.requestPermission();
    console.log(permission);
    if (permission === "granted") {
      const fcmToken = await getToken(messaging, {
        vapidKey: "BGhsF4rzwu-OoIR-C6xnTxj2pEgpEt4R7v8KPwZb9ts1Q-ttNKVm_Jt0dvnnjU-JvN9DV_jG2rPTH1DFHIX-9Vc",
      });
      if (fcmToken) {
        return fcmToken;
      } else {
        throw new Error("No registration token available");
      }
    } else {
      throw new Error("Notification permission not granted");
    }
  } catch (error) {
    throw new Error(`Notification permission error: ${error}`);
  }
};

export const requestNotificationPermissionOnGesture = async (): Promise<string | null> => {
  try {
    if (typeof window === "undefined" || !("Notification" in window)) {
      return null;
    }
    const permission = await Notification.requestPermission();
    if (permission === "granted") {
      const fcmToken = await getToken(messaging, {
        vapidKey: "BGhsF4rzwu-OoIR-C6xnTxj2pEgpEt4R7v8KPwZb9ts1Q-ttNKVm_Jt0dvnnjU-JvN9DV_jG2rPTH1DFHIX-9Vc",
      });
      if (fcmToken) {
        localStorage.setItem("fcmToken", fcmToken);
        return fcmToken;
      }
    }
    return null;
  } catch (error) {
    console.warn("FCM permission request failed on user gesture:", error);
    return null;
  }
};

export const onMessageListener = () => {
  return new Promise((resolve) => {
    onMessage(messaging, (payload: any) => {
      resolve(payload);
    });
  });
};

// Store Firebase phone auth confirmation result for OTP verification (login flow)
let phoneAuthConfirmationResult: ConfirmationResult | null = null;

// Global singleton reCAPTCHA verifier to prevent re-rendering issues
let globalRecaptchaVerifier: RecaptchaVerifier | null = null;
let globalWidgetId: number | null = null;
const GLOBAL_RECAPTCHA_CONTAINER_ID = "global-recaptcha-container";

export function setPhoneAuthConfirmationResult(result: ConfirmationResult | null): void {
  phoneAuthConfirmationResult = result;
}

export function getPhoneAuthConfirmationResult(): ConfirmationResult | null {
  return phoneAuthConfirmationResult;
}

/**
 * Reset the global reCAPTCHA widget without destroying it.
 * This allows reuse across multiple OTP requests.
 */
function resetGlobalRecaptcha() {
  if (globalWidgetId !== null && typeof window !== "undefined" && (window as any).grecaptcha) {
    try {
      (window as any).grecaptcha.reset(globalWidgetId);
    } catch (error) {
      console.debug("grecaptcha.reset failed:", error);
    }
  }
}

/**
 * Completely destroy the global reCAPTCHA verifier.
 * Only call this if you need to fully reinitialize (rare).
 */
function destroyGlobalRecaptcha() {
  // Reset the widget first
  resetGlobalRecaptcha();

  // Clear the verifier instance
  if (globalRecaptchaVerifier) {
    try {
      globalRecaptchaVerifier.clear();
    } catch (error) {
      console.debug("RecaptchaVerifier.clear failed:", error);
    }
    globalRecaptchaVerifier = null;
  }

  globalWidgetId = null;

  // Clean up the container DOM
  const container = document.getElementById(GLOBAL_RECAPTCHA_CONTAINER_ID);
  if (container) {
    try {
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
    } catch (error) {
      console.debug("DOM cleanup failed:", error);
    }
  }
}

/**
 * Export cleanup function for backward compatibility.
 * This now just resets the global verifier instead of destroying it.
 */
export function cleanupRecaptchaContainer(container?: HTMLElement | null) {
  // Just reset the global verifier, don't destroy it
  resetGlobalRecaptcha();
}

/**
 * Get or create the global reCAPTCHA verifier.
 * This ensures we only create one instance that persists.
 */
async function getOrCreateGlobalRecaptchaVerifier(): Promise<RecaptchaVerifier> {
  // If verifier already exists, just reset it for reuse
  if (globalRecaptchaVerifier) {
    resetGlobalRecaptcha();
    return globalRecaptchaVerifier;
  }

  // Get the global container
  const container = document.getElementById(GLOBAL_RECAPTCHA_CONTAINER_ID);
  if (!container) {
    throw new Error(
      `Global reCAPTCHA container with id "${GLOBAL_RECAPTCHA_CONTAINER_ID}" not found. ` +
        "Make sure GlobalModal component is mounted."
    );
  }

  // Create new verifier
  globalRecaptchaVerifier = new RecaptchaVerifier(auth, container, {
    size: "invisible",
    callback: () => {
      console.debug("reCAPTCHA solved");
    },
  });

  // Render and store widget ID
  try {
    globalWidgetId = await globalRecaptchaVerifier.render();
    console.debug("Global reCAPTCHA initialized with widget ID:", globalWidgetId);
  } catch (error) {
    console.error("Failed to render global reCAPTCHA:", error);
    globalRecaptchaVerifier = null;
    throw error;
  }

  return globalRecaptchaVerifier;
}

/**
 * Send OTP to the given phone number using Firebase Auth.
 * Phone number must be in E.164 format (e.g. +97312345678).
 * @param phoneNumber - E.164 format: +countryCode + number
 * @param recaptchaContainer - (DEPRECATED) No longer used, kept for backward compatibility
 */
export async function sendPhoneOtp(phoneNumber: string, recaptchaContainer?: HTMLElement): Promise<ConfirmationResult> {
  try {
    // Get or create the global verifier (resets if already exists)
    const recaptchaVerifier = await getOrCreateGlobalRecaptchaVerifier();

    // Send OTP using the global verifier
    const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
    phoneAuthConfirmationResult = confirmationResult;
    return confirmationResult;
  } catch (err) {
    // On error, reset the verifier for retry
    resetGlobalRecaptcha();
    throw err;
  }
}

/**
 * Verify the OTP code and return Firebase idToken.
 * Uses the confirmation result from the last sendPhoneOtp call (login flow).
 * Keeps the confirmation session on invalid code so the user can retry.
 */
export async function verifyPhoneOtp(code: string): Promise<{ userCredential: UserCredential; idToken: string }> {
  if (!phoneAuthConfirmationResult) {
    const error = new Error("INVALID_CODE");
    (error as Error & { code: string }).code = "auth/invalid-verification-code";
    throw error;
  }

  try {
    const userCredential = await phoneAuthConfirmationResult.confirm(code);
    const idToken = await userCredential.user.getIdToken();
    // Clear only after a successful verification
    phoneAuthConfirmationResult = null;
    return { userCredential, idToken };
  } catch (err: unknown) {
    const codeName =
      typeof err === "object" && err !== null && "code" in err
        ? String((err as { code?: string }).code || "")
        : "";
    const message = err instanceof Error ? err.message : String(err ?? "");

    // Session is no longer usable — clear so user must resend
    if (
      codeName === "auth/code-expired" ||
      codeName === "auth/session-expired" ||
      /code-expired|session-expired/i.test(message)
    ) {
      phoneAuthConfirmationResult = null;
    }

    // Normalize invalid/expired OTP errors for the UI
    if (
      codeName === "auth/invalid-verification-code" ||
      codeName === "auth/code-expired" ||
      codeName === "auth/session-expired" ||
      /invalid-verification-code|code-expired|session-expired|INVALID_CODE/i.test(message)
    ) {
      const error = new Error("INVALID_CODE");
      (error as Error & { code: string }).code = codeName || "auth/invalid-verification-code";
      throw error;
    }

    throw err;
  }
}

export function isFirebaseInvalidOtpError(error: unknown): boolean {
  const code =
    typeof error === "object" && error !== null && "code" in error
      ? String((error as { code?: string }).code || "")
      : "";
  const message = error instanceof Error ? error.message : String(error ?? "");
  return (
    message === "INVALID_CODE" ||
    code === "auth/invalid-verification-code" ||
    code === "auth/code-expired" ||
    code === "auth/session-expired" ||
    /invalid-verification-code|code-expired|session-expired|INVALID_CODE/i.test(message)
  );
}

